import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { MessageSquare, X, Send, Sparkles } from "lucide-react";
import { CHAT } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "Modular kitchen cost?",
  "How long does a 3BHK take?",
  "Do you offer a free site visit?",
];

export const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm Ira, your design consultant at Urban Renovations. How can I help transform your space today?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId });
      setSessionId(res.data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (_) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm briefly unavailable — please call us at +91 95999 20839 and our team will help right away." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={CHAT.toggle}
        onClick={() => setOpen((v) => !v)}
        className="fixed left-4 sm:left-auto sm:right-6 bottom-24 sm:bottom-6 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-black to-ink text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Chat with Ira"
      >
        {open ? <X className="h-6 w-6" /> : <MessageSquare className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={CHAT.panel}
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="fixed left-4 sm:left-auto sm:right-6 bottom-40 sm:bottom-24 z-40 w-[calc(100vw-2rem)] sm:w-[380px] h-[520px] max-h-[65vh] rounded-3xl overflow-hidden shadow-2xl bg-surface border border-hairline flex flex-col"
          >
            <div className="bg-ink text-canvas p-4 flex items-center gap-3">
              <span className="h-10 w-10 rounded-full bg-gradient-to-br from-black to-ink text-white flex items-center justify-center"><Sparkles className="h-5 w-5" /></span>
              <div className="flex-1">
                <div className="font-heading font-bold text-sm">Ira · AI Design Consultant</div>
                <div className="text-[11px] text-canvas/60 flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-green-400" /> Online now</div>
              </div>
              <button type="button" data-testid={CHAT.close} onClick={() => setOpen(false)} className="h-8 w-8 rounded-full hover:bg-white/10 flex items-center justify-center" aria-label="Close chat"><X className="h-4 w-4" /></button>
            </div>

            <div data-testid={CHAT.messages} className="flex-1 overflow-y-auto p-4 space-y-3 bg-canvas">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "bg-black text-white rounded-br-md" : "bg-surface border border-hairline text-ink rounded-bl-md"}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-surface border border-hairline rounded-2xl rounded-bl-md px-4 py-3 flex gap-1">
                    {[0, 1, 2].map((d) => <span key={d} className="h-2 w-2 rounded-full bg-ink-soft/50 animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />)}
                  </div>
                </div>
              )}
              {messages.length <= 1 && (
                <div className="pt-2 flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s) => (
                    <button key={s} type="button" onClick={() => send(s)} className="text-xs px-3 py-1.5 rounded-full border border-hairline text-ink-soft hover:border-ink hover:text-ink transition-colors">{s}</button>
                  ))}
                </div>
              )}
              <div ref={endRef} />
            </div>

            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-hairline bg-surface flex items-center gap-2">
              <input
                data-testid={CHAT.input}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about pricing, timelines…"
                className="flex-1 h-11 rounded-full bg-canvas border border-hairline px-4 text-sm text-ink placeholder:text-ink-soft/60 focus:outline-none focus:border-ink"
              />
              <button type="submit" data-testid={CHAT.send} disabled={busy} className="h-11 w-11 rounded-full bg-black text-white flex items-center justify-center hover:bg-ink transition-colors disabled:opacity-50" aria-label="Send"><Send className="h-5 w-5" /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
